import type {
  PlanningEntityIdentifier,
  PlanningEntityType
} from 'generated/index';

import type {
  OrganizationalUnitId,
  OrganizationalUnitOption
} from 'utils/organizationalUnits';
import type { EntitySearchOption } from 'components/mrp/components/EntitySearch/EntitySearch.types';

/** Whose plan unit: the care receivers in treatment under it, or the employees employed on it. */
export const ORGANIZATIONAL_UNIT_PERSPECTIVES = ['CLIENT', 'EMPLOYEE'] as const;
export type OrganizationalUnitPerspective =
  (typeof ORGANIZATIONAL_UNIT_PERSPECTIVES)[number];

/** A type chip: "Alle", or one type — a people type, or `ORGANIZATIONAL_UNIT` for plan units. */
export type PlanningEntityTypeFilterOption = 'ALL' | PlanningEntityType;

export type PlanningEntityEntitySelection = {
  kind: 'ENTITY';
  planningEntity: PlanningEntityIdentifier;
  label: string;
};

export type PlanningEntityOrganizationalUnitSelection = {
  kind: 'ORGANIZATIONAL_UNIT';
  /** The plan unit or plan group by `uniqId`: a number for a plan unit, "5-group" for a plan group. */
  organizationalUnitId: OrganizationalUnitId;
  label: string;
  perspective: OrganizationalUnitPerspective;
};

/** One chip of the picker: a single person, or the people of a plan unit seen from one perspective. */
export type PlanningEntitySelection =
  | PlanningEntityEntitySelection
  | PlanningEntityOrganizationalUnitSelection;

export type PlanningEntityPickerOption =
  | {
      kind: 'ENTITY';
      key: string;
      searchOption: EntitySearchOption;
      selection: PlanningEntityEntitySelection;
    }
  | {
      kind: 'ORGANIZATIONAL_UNIT';
      key: string;
      organizationalUnit: OrganizationalUnitOption;
      /** The selection a plain pick of the row adds: from its default perspective. */
      selection: PlanningEntityOrganizationalUnitSelection;
      /** The perspectives the row still offers — those not picked yet — the default first. */
      perspectives: OrganizationalUnitPerspective[];
    };

export type PlanningEntityPickerProps = {
  /** The chips, in the order they were picked. */
  value: PlanningEntitySelection[];
  /** Called with the whole next selection: after a pick (one chip added) or a removal. */
  onChange: (next: PlanningEntitySelection[]) => void;
  /** The people types to search: employees, clients, resources. */
  entityTypes: PlanningEntityType[];
  /** Also offer the user's plan units and plan groups. Default: `false`. */
  searchOrganizationalUnits?: boolean;
  /**
   * The perspectives a plan unit is offered in, on its one row; the first is the default a plain pick
   * uses. Default: patients, employees.
   */
  organizationalUnitPerspectives?: readonly OrganizationalUnitPerspective[];
  /**
   * The type chips shown, in this order. A type not searched here (not in `entityTypes`, or plan units
   * without `searchOrganizationalUnits`) is left out. Default: Alle, the `entityTypes`, and Planeenheden
   * when plan units are searchable. An empty list hides the chip row.
   */
  typeFilters?: readonly PlanningEntityTypeFilterOption[];
  /**
   * The chip that is on when the picker mounts. Default: Alle — or, when Alle is not shown, the first
   * chip shown. A chip that is not shown is ignored.
   */
  defaultTypeFilter?: PlanningEntityTypeFilterOption;
  placeholder?: string;
  minWidth?: number;
  /**
   * Show the picked chips after the search. Default: `true`. Off when the page lists them itself — with
   * `PlanningEntitySelectionChips` — together with its other applied filters.
   */
  showSelection?: boolean;
};
